import { useEffect, useState } from "react";
import {
  FiX,
  FiTruck,
  FiAlertCircle,
  FiLoader,
  FiAnchor,
} from "react-icons/fi";

const defaultForm = {
  productName: "",
  supplier: "",
  categoryId: "",
  cost: "",
  shipping: "",
  status: "Active",
  isActive: true,
};

const getErrorMessage = (err) => {
  return (
    err?.response?.data?.message ||
    err?.message ||
    "Something went wrong. Please try again."
  );
};

export default function AddCostPriceModal({
  isOpen,
  onClose,
  onSave,
  editingPrice,
  categories = [],
}) {
  const [form, setForm] = useState(defaultForm);
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (!isOpen) return;

    setError("");

    if (editingPrice) {
      setForm({
        productName: editingPrice.productName || "",
        supplier: editingPrice.supplier || "",
        categoryId: String(
          editingPrice.categoryId || editingPrice.category?.id || ""
        ),
        cost: String(editingPrice.cost ?? ""),
        shipping: String(editingPrice.shipping ?? ""),
        status: editingPrice.status || "Active",
        isActive: editingPrice.isActive ?? true,
      });
    } else {
      setForm({
        ...defaultForm,
        categoryId: categories?.[0]?.id ? String(categories[0].id) : "",
      });
    }
  }, [editingPrice, isOpen, categories]);

  if (!isOpen) return null;

  const freightCost = Number(form.cost || 0);
  const localCharges = Number(form.shipping || 0);
  const total = freightCost + localCharges;

  const handleClose = () => {
    if (saving) return;
    setError("");
    onClose();
  };

  const handleSubmit = async (e) => {
    e.preventDefault();

    setError("");

    if (!form.productName.trim()) {
      setError("Route / container detail is required.");
      return;
    }

    if (!form.supplier.trim()) {
      setError("Carrier / forwarder is required.");
      return;
    }

    if (!form.categoryId) {
      setError("Category is required.");
      return;
    }

    if (freightCost < 0 || localCharges < 0) {
      setError("Costs cannot be negative.");
      return;
    }

    setSaving(true);

    try {
      await onSave({
        productName: form.productName.trim(),
        supplier: form.supplier.trim(),
        categoryId: Number(form.categoryId),
        cost: freightCost,
        shipping: localCharges,
        status: form.status,
        isActive: form.isActive,
      });
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  const inputClass =
    "w-full rounded-xl border border-slate-200 bg-white px-4 py-3 text-sm outline-none transition placeholder:text-slate-400 focus:border-blue-500 focus:ring-4 focus:ring-blue-50 disabled:bg-slate-100 disabled:text-slate-500 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-100 dark:placeholder:text-slate-500 dark:focus:ring-blue-950";

  const labelClass =
    "mb-2 block text-sm font-semibold text-slate-700 dark:text-slate-300";

  return (
    <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-slate-950/60 p-4 backdrop-blur-sm">
      <div className="max-h-[92vh] w-full max-w-3xl overflow-hidden rounded-3xl border border-slate-100 bg-white shadow-2xl dark:border-slate-800 dark:bg-slate-900">
        <div className="flex items-start justify-between border-b border-slate-100 px-6 py-5 dark:border-slate-800">
          <div className="flex items-start gap-4">
            <div className="grid h-12 w-12 shrink-0 place-items-center rounded-2xl bg-blue-50 text-blue-600 dark:bg-blue-950 dark:text-blue-300">
              <FiTruck className="text-xl" />
            </div>

            <div>
              <h2 className="text-xl font-bold text-slate-950 dark:text-white">
                {editingPrice ? "Edit Freight Rate" : "Add Freight Rate"}
              </h2>
              <p className="mt-1 text-sm text-slate-500 dark:text-slate-400">
                Add China to UK container freight and local charges.
              </p>
            </div>
          </div>

          <button
            onClick={handleClose}
            disabled={saving}
            className="grid h-10 w-10 place-items-center rounded-full border border-slate-200 text-slate-500 transition hover:bg-slate-50 hover:text-slate-700 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:text-slate-400 dark:hover:bg-slate-800 dark:hover:text-slate-200"
            type="button"
            aria-label="Close modal"
          >
            <FiX className="text-lg" />
          </button>
        </div>

        <form
          onSubmit={handleSubmit}
          className="max-h-[calc(92vh-90px)] overflow-y-auto p-6"
        >
          {error ? (
            <div className="mb-5 flex items-start gap-3 rounded-2xl border border-red-100 bg-red-50 px-4 py-3 text-sm text-red-700 dark:border-red-900 dark:bg-red-950 dark:text-red-300">
              <FiAlertCircle className="mt-0.5 shrink-0" />
              <span>{error}</span>
            </div>
          ) : null}

          <div className="grid grid-cols-1 gap-5 md:grid-cols-2">
            <div>
              <label className={labelClass}>Route / Container Detail *</label>
              <input
                type="text"
                value={form.productName}
                onChange={(e) =>
                  setForm({ ...form, productName: e.target.value })
                }
                className={inputClass}
                placeholder="e.g. Shanghai to Felixstowe - 40HQ"
                disabled={saving}
              />
            </div>

            <div>
              <label className={labelClass}>Carrier / Forwarder *</label>
              <input
                type="text"
                value={form.supplier}
                onChange={(e) =>
                  setForm({ ...form, supplier: e.target.value })
                }
                className={inputClass}
                placeholder="e.g. COSCO, Maersk, ABC Logistics"
                disabled={saving}
              />
            </div>

            <div>
              <label className={labelClass}>Route Category *</label>
              <select
                value={form.categoryId}
                onChange={(e) =>
                  setForm({ ...form, categoryId: e.target.value })
                }
                className={inputClass}
                disabled={saving}
              >
                <option value="">Select category</option>
                {categories.map((cat) => (
                  <option key={cat.id} value={cat.id}>
                    {cat.name}
                  </option>
                ))}
              </select>
            </div>

            <div>
              <label className={labelClass}>Status</label>
              <select
                value={form.status}
                onChange={(e) => setForm({ ...form, status: e.target.value })}
                className={inputClass}
                disabled={saving}
              >
                <option value="Active">Active</option>
                <option value="Inactive">Inactive</option>
              </select>
            </div>

            <div>
              <label className={labelClass}>Ocean Freight Cost (£) *</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.cost}
                onChange={(e) => setForm({ ...form, cost: e.target.value })}
                className={inputClass}
                placeholder="0.00"
                disabled={saving}
              />
            </div>

            <div>
              <label className={labelClass}>UK / Delivery Charges (£)</label>
              <input
                type="number"
                step="0.01"
                min="0"
                value={form.shipping}
                onChange={(e) =>
                  setForm({ ...form, shipping: e.target.value })
                }
                className={inputClass}
                placeholder="0.00"
                disabled={saving}
              />
            </div>
          </div>

          <div className="mt-5 rounded-2xl border border-blue-100 bg-blue-50 px-4 py-4 text-sm text-slate-700 dark:border-blue-900 dark:bg-blue-950 dark:text-slate-300">
            <div className="flex items-center gap-2">
             <FiAnchor className="text-blue-600 dark:text-blue-300" />
              <span>
                Total Freight Cost:{" "}
                <strong className="text-slate-950 dark:text-white">
                  £{total.toFixed(2)}
                </strong>
              </span>
            </div>
          </div>

          <label className="mt-5 flex items-center gap-3 rounded-2xl border border-slate-200 bg-slate-50 px-4 py-3 text-sm text-slate-700 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300">
            <input
              type="checkbox"
              checked={form.isActive}
              onChange={(e) =>
                setForm({ ...form, isActive: e.target.checked })
              }
              disabled={saving}
              className="h-4 w-4 rounded border-slate-300 text-blue-600 focus:ring-blue-500 dark:border-slate-600"
            />
            Active
          </label>

          <div className="mt-5 flex justify-end gap-3 border-t border-slate-100 pt-4 dark:border-slate-800">
            <button
              type="button"
              onClick={handleClose}
              disabled={saving}
              className="rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-700 transition hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-60 dark:border-slate-700 dark:bg-slate-950 dark:text-slate-300 dark:hover:bg-slate-800"
            >
              Cancel
            </button>

            <button
              type="submit"
              disabled={saving}
              className="inline-flex items-center gap-2 rounded-xl bg-blue-600 px-5 py-3 text-sm font-semibold text-white transition hover:bg-blue-700 disabled:cursor-not-allowed disabled:opacity-60"
            >
              {saving ? (
                <>
                  <FiLoader className="animate-spin" />
                  Saving...
                </>
              ) : editingPrice ? (
                "Update Rate"
              ) : (
                "Save Rate"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}